import { useState } from 'react';
import { toast } from 'sonner';
import { useGmbActivityLog, useDownloadActivityLogCsv } from '../../hooks/useGmbGovernance';
import GMBEmptyState from './shared/GMBEmptyState';
import { GBMContentWrapper } from './shared/GMBLoadingState';

const actionTypes = [
  'All',
  'photo_upload',
  'photo_delete',
  'video_upload',
  'video_delete',
  'post_edit',
  'post_image_edit',
  'publish',
  'schedule',
  'review_ai_generated',
  'review_response',
  'qa_answer',
  'qa_flag',
];
const statusTypes = ['All', 'success', 'in_progress', 'failed'];

const statusStyle: Record<string, string> = {
  success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  in_progress: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

const actionIconMap: Record<string, string> = {
  photo_upload: 'ri-image-add-line',
  photo_delete: 'ri-delete-bin-line',
  post_edit: 'ri-edit-line',
  post_image_edit: 'ri-image-edit-line',
  publish: 'ri-send-plane-line',
  schedule: 'ri-calendar-schedule-line',
  review_ai_generated: 'ri-sparkling-line',
  review_response: 'ri-chat-check-line',
  qa_answer: 'ri-question-answer-line',
  qa_flag: 'ri-flag-line',
};

const actionColorMap: Record<string, string> = {
  photo_upload: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  photo_delete: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-300',
  post_edit: 'text-sky-600 bg-sky-50 dark:bg-sky-900/20 dark:text-sky-300',
  publish: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20 dark:text-rose-300',
  schedule: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-300',
  post_image_edit: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-300',
  review_ai_generated: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-300',
  review_response: 'text-teal-600 bg-teal-50 dark:bg-teal-900/20 dark:text-teal-300',
  qa_answer: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 dark:text-indigo-300',
  qa_flag: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-300',
};

function downloadCsv(content: string | Blob, filename: string) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function ActivityLog() {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState('All Time');
  const [actionFilter, setActionFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const timeFilterMap: Record<string, string> = {
    'All Time': 'all_time',
    Today: 'today',
    'Last 7 days': 'this_week',
    'Last 30 days': 'this_month',
  };

  const { data: logData, isLoading, error, refetch, isFetching } = useGmbActivityLog({
    page: currentPage,
    pageSize: itemsPerPage,
    timeFilter: timeFilterMap[dateFilter] || 'all_time',
    actionFilter: actionFilter === 'All' ? 'all' : actionFilter,
    statusFilter: statusFilter === 'All' ? 'all' : statusFilter.toLowerCase().replace(' ', '_'),
  });

  const exportCsv = useDownloadActivityLogCsv();

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await exportCsv.mutateAsync();
      downloadCsv(blob, `gmb-activity-log-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Activity log exported successfully');
    } catch (err) {
      toast.error('Failed to export activity log');
    } finally {
      setExporting(false);
    }
  };

  const data = logData?.data || {};
  const activities = data.activities || [];
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / itemsPerPage));

  const stats = [
    { label: 'Total Activities', value: data.total ?? 0, icon: 'ri-history-line', color: 'text-gray-600 bg-gray-100' },
    { label: 'Completed', value: data.completed ?? 0, icon: 'ri-check-line', color: 'text-green-600 bg-green-50' },
    { label: 'In Progress', value: data.in_progress ?? 0, icon: 'ri-loader-line', color: 'text-amber-600 bg-amber-50' },
    { label: 'Failed', value: data.failed ?? 0, icon: 'ri-error-warning-line', color: 'text-red-600 bg-red-50' },
  ];

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-7 h-7 ${s.color} rounded-md flex items-center justify-center`}>
                  <i className={`${s.icon} text-xs`} />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{s.label}</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setExportMenuOpen(!exportMenuOpen)}
                disabled={exporting}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                <i className={exporting ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} />
                {exporting ? 'Exporting…' : 'Export'}
                <i className="ri-arrow-down-s-line" />
              </button>
              {exportMenuOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                  <button
                    onClick={() => { setExportMenuOpen(false); handleExportCsv(); }}
                    disabled={exporting}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    CSV
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={dateFilter}
              onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800"
            >
              <option>All Time</option>
              <option>Today</option>
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>

            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800"
            >
              {actionTypes.map((a) => (
                <option key={a} value={a}>{a === 'All' ? 'All Actions' : a.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800"
            >
              {statusTypes.map((s) => (
                <option key={s} value={s}>{s === 'All' ? 'All Status' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((row: any) => {
                  const iconClass = actionIconMap[row.action_type] || 'ri-file-list-line';
                  const colorClass = actionColorMap[row.action_type] || 'text-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-300';
                  return (
                    <tr key={row.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                            <i className={`${iconClass} text-sm`} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{row.description || row.action_type}</div>
                            <div className="text-[11px] text-gray-400">{row.entity_type} · Performed by {row.performed_by}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 dark:text-gray-300">{row.date_label || row.timestamp_label}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusStyle[row.status] || statusStyle.success}`}>
                          {row.status === 'success' && <i className="ri-check-line" />}
                          {row.status === 'in_progress' && <i className="ri-loader-line animate-spin" />}
                          {row.status === 'failed' && <i className="ri-close-line" />}
                          {row.status === 'success' ? 'Completed' : row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {activities.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-10">
                      <GMBEmptyState title="No activity logs" message="No activity logs match your filters." icon="ri-search-line" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {isFetching ? 'Loading...' : `Showing ${activities.length} of ${data.total || 0} entries`}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="px-3 py-1 text-xs text-gray-600 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-2 py-1 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </GBMContentWrapper>
  );
}